import { Typography, useTheme } from '@material-ui/core';
import { format, parseISO } from 'date-fns';
import React from 'react';
import { useTranslate } from 'react-admin';

import type { StorageEntityInfo } from '../types/warehouse.types';
import { formatFloat } from '../utils/numberFormatters';
import { formatProductLabel } from '../utils/palletFormatters';
import { groupByStack } from '../utils/stackHelpers';
import { InfoField } from './InfoField';
import { OrderLink } from './OrderLink';
import { valueTextStyle } from './styles';

interface MultiplePalletsInfoProps {
  storageEntities: StorageEntityInfo[];
  showExtendedInfo?: boolean;
}

export const MultiplePalletsInfo: React.FC<MultiplePalletsInfoProps> = ({
  storageEntities,
  showExtendedInfo = true,
}) => {
  const theme = useTheme();
  const translate = useTranslate();
  const dynamicTextStyle = valueTextStyle(theme);

  const missingValueLabel = translate('osfc.storage_ui.shared.missing_value');

  // Stacked records (same stack_group_id) are one physical pallet
  const physicalPalletsCount = groupByStack(storageEntities).length;
  const hasStackedPallets = physicalPalletsCount !== storageEntities.length;

  const uniqueIncomingOrders = Array.from(new Set(storageEntities.map(p => p.order_ref).filter(Boolean)));
  const uniqueOutgoingOrders = Array.from(new Set(storageEntities.filter(p => p.reserved_order_ref).map(p => p.reserved_order_ref)));
  const totalBoxes = storageEntities.reduce((sum, p) => sum + (p.boxes ?? 0), 0);
  const totalKilos = storageEntities.reduce((sum, p) => sum + (p.kilos ?? 0), 0);
  const uniqueProducts = Array.from(new Set(storageEntities.map(p => formatProductLabel(p.product_number, p.product_name, missingValueLabel))));
  const uniqueCustomer = Array.from(new Set(storageEntities.map(p => p.customer_name).filter(Boolean)));

  const productionDates = storageEntities.map(p => parseISO(p.production_date ?? '')).filter(d => !isNaN(d.getTime()));
  const expirationDates = storageEntities.map(p => parseISO(p.expiration_date ?? '')).filter(d => !isNaN(d.getTime()));

  const minProductionDate = productionDates.length > 0
    ? format(new Date(Math.min(...productionDates.map(d => d.getTime()))), 'yyyy-MM-dd')
    : missingValueLabel;
  const maxProductionDate = productionDates.length > 0
    ? format(new Date(Math.max(...productionDates.map(d => d.getTime()))), 'yyyy-MM-dd')
    : missingValueLabel;

  const minExpirationDate = expirationDates.length > 0
    ? format(new Date(Math.min(...expirationDates.map(d => d.getTime()))), 'yyyy-MM-dd')
    : missingValueLabel;
  const maxExpirationDate = expirationDates.length > 0
    ? format(new Date(Math.max(...expirationDates.map(d => d.getTime()))), 'yyyy-MM-dd')
    : missingValueLabel;

  const productionDateDisplay = minProductionDate === maxProductionDate
    ? minProductionDate
    : `${minProductionDate} - ${maxProductionDate}`;

  const expirationDateDisplay = minExpirationDate === maxExpirationDate
    ? minExpirationDate
    : `${minExpirationDate} - ${maxExpirationDate}`;

  return (
    <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: `2px solid ${theme.palette.primary.main}` }}>
      <Typography component="p" variant="subtitle2" style={{ margin: 0, fontWeight: 'bold', marginBottom: '10px', color: theme.palette.primary.main }}>
        {translate('osfc.storage_ui.multiple_pallets.title')} ({storageEntities.length})
      </Typography>
      {hasStackedPallets && (
        <InfoField
          label={translate('osfc.storage_ui.multiple_pallets.physical_pallets')}
          value={physicalPalletsCount}
          valueStyle={dynamicTextStyle}
        />
      )}
      <InfoField label={translate('osfc.storage_ui.multiple_pallets.customers')} value={uniqueCustomer.join(', ').toUpperCase()} valueStyle={dynamicTextStyle} />
      <Typography component="div" variant="body2">
        <strong>{translate('osfc.storage_ui.multiple_pallets.incoming_orders')}</strong>
        {uniqueIncomingOrders.map((orderRef, idx) => {
          const pallet = storageEntities.find(p => p.order_ref === orderRef);
          return (
            <span key={orderRef}>
              <OrderLink orderId={pallet?.order_id} orderRef={orderRef} />
              {idx < uniqueIncomingOrders.length - 1 ? ', ' : ''}
            </span>
          );
        })}
      </Typography>
      {uniqueOutgoingOrders.length > 0 && (
        <Typography component="div" variant="body2">
          <strong>{translate('osfc.storage_ui.multiple_pallets.outgoing_orders')}</strong>
          {uniqueOutgoingOrders.map((orderRef, idx) => {
            const pallet = storageEntities.find(p => p.reserved_order_ref === orderRef);
            return (
              <span key={orderRef}>
                <OrderLink orderId={pallet?.reserved_order_id} orderRef={orderRef} />
                {idx < uniqueOutgoingOrders.length - 1 ? ', ' : ''}
              </span>
            );
          })}
        </Typography>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        <InfoField label={translate('osfc.storage_ui.multiple_pallets.total_boxes')} value={totalBoxes} valueStyle={dynamicTextStyle} />
        <InfoField label={translate('osfc.storage_ui.multiple_pallets.total_kilos')} value={formatFloat(totalKilos)} valueStyle={dynamicTextStyle} />
      </div>
      <InfoField label={translate('osfc.storage_ui.multiple_pallets.products')} value={uniqueProducts.join(', ')} valueStyle={dynamicTextStyle} />
      {showExtendedInfo && (
        <>
          <InfoField label={translate('osfc.storage_ui.multiple_pallets.production')} value={productionDateDisplay} valueStyle={dynamicTextStyle} />
          <InfoField label={translate('osfc.storage_ui.multiple_pallets.expiration')} value={expirationDateDisplay} valueStyle={dynamicTextStyle} />
        </>
      )}
    </div>
  );
};
